import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { AccountBalance as BankIcon, VerifiedUser as VerifyIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import client from '../../API';

/**
 * ManualBankEntryForm — Form for manually entering bank routing/account
 * numbers. Submits to the platform-level manual-bank endpoint (no Connect).
 */
const ManualBankEntryForm = ({ onSuccess, onError, sx = {} }) => {
  const [form, setForm] = useState({
    routing_number: '',
    account_number: '',
    account_holder_name: '',
    account_type: 'checking',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [probing, setProbing] = useState(true);
  const hasProbed = useRef(false);

  // On mount: (1) reconcile banks already attached to the user's Connect
  // account (e.g. linked during Stripe-hosted onboarding) so the user
  // doesn't have to re-enter them here; (2) probe onboarding state so the
  // submit button shows "Complete Stripe Verification" up front instead of
  // failing on submit. Both calls are best-effort — errors fall back to
  // today's submit-then-detect behavior.
  useEffect(() => {
    if (hasProbed.current) return;
    hasProbed.current = true;
    (async () => {
      try {
        const reconciled = await client.bank.reconcileExternalAccounts();
        const wallet = reconciled?.wallets?.[0];
        if (wallet) {
          onSuccess(wallet);
          return;
        }
        if (reconciled && reconciled.has_account === false) {
          setNeedsOnboarding(true);
          return;
        }
        const status = await client.bank.getAccountStatus();
        // Only flag as needing onboarding if the user hasn't even submitted
        // the Stripe form yet. details_submitted is set by Stripe once the
        // AccountLink form is completed, even if capabilities are still pending.
        // Without this guard, users who finished onboarding would still see
        // "Complete Stripe Verification" while Stripe processes their KYC.
        if (!status?.has_account || (status?.onboarding_incomplete && !status?.details_submitted)) {
          setNeedsOnboarding(true);
        }
      } catch {
        // Swallow — submit path will still surface express_onboarding_required.
      } finally {
        setProbing(false);
      }
    })();
  }, [onSuccess]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    if (field === 'routing_number') {
      // Only allow digits, max 9
      setForm((prev) => ({ ...prev, [field]: value.replace(/\D/g, '').slice(0, 9) }));
    } else if (field === 'account_number') {
      // Only allow digits, max 17
      setForm((prev) => ({ ...prev, [field]: value.replace(/\D/g, '').slice(0, 17) }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const isValid =
    form.routing_number.length === 9 &&
    form.account_number.length >= 4 &&
    form.account_holder_name.trim().length > 0;

  const handleExpressOnboarding = async () => {
    setLoading(true);
    setError(null);
    try {
      await client.bank.createExpressAccount();
      const refreshUrl = window.location.href;
      const returnUrl = (() => {
        const u = new URL(window.location.href);
        u.searchParams.set('stripe_onboarding', 'complete');
        return u.toString();
      })();
      const linkResponse = await client.bank.getExpressOnboardingLink({
        return_url: returnUrl,
        refresh_url: refreshUrl,
      });
      if (linkResponse.onboarding_url) {
        window.location.href = linkResponse.onboarding_url;
      } else {
        throw new Error('No onboarding URL received');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to start Stripe verification';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsOnboarding) {
      await handleExpressOnboarding();
      return;
    }
    if (!isValid || loading) return;

    setLoading(true);
    setError(null);

    try {
      const walletData = await client.bank.createManualBank({
        routing_number: form.routing_number,
        account_number: form.account_number,
        account_holder_name: form.account_holder_name.trim(),
        account_type: form.account_type,
      });
      onSuccess(walletData);
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      if (errorCode === 'express_onboarding_required') {
        setNeedsOnboarding(true);
      } else {
        const errorMessage =
          err.response?.data?.error || err.message || 'Failed to add bank account';
        setError(errorMessage);
        if (onError) onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={sx}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        <TextField
          label="Account Holder Name"
          value={form.account_holder_name}
          onChange={handleChange('account_holder_name')}
          required
          fullWidth
          size="small"
          inputProps={{ maxLength: 255 }}
        />

        <TextField
          label="Routing Number"
          value={form.routing_number}
          onChange={handleChange('routing_number')}
          required
          fullWidth
          size="small"
          placeholder="9 digits"
          helperText={
            form.routing_number.length > 0 && form.routing_number.length < 9
              ? `${9 - form.routing_number.length} digits remaining`
              : ''
          }
          inputProps={{ maxLength: 9, inputMode: 'numeric' }}
        />

        <TextField
          label="Account Number"
          value={form.account_number}
          onChange={handleChange('account_number')}
          required
          fullWidth
          size="small"
          placeholder="4-17 digits"
          inputProps={{ maxLength: 17, inputMode: 'numeric' }}
        />

        <FormControl fullWidth size="small">
          <InputLabel>Account Type</InputLabel>
          <Select
            value={form.account_type}
            onChange={handleChange('account_type')}
            label="Account Type"
          >
            <MenuItem value="checking">Checking</MenuItem>
            <MenuItem value="savings">Savings</MenuItem>
          </Select>
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          color={needsOnboarding ? 'warning' : 'primary'}
          disabled={probing || (!needsOnboarding && !isValid) || loading}
          startIcon={
            probing || loading
              ? <CircularProgress size={20} />
              : needsOnboarding
                ? <VerifyIcon />
                : <BankIcon />
          }
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          {probing
            ? 'Loading...'
            : loading
              ? (needsOnboarding ? 'Redirecting...' : 'Adding Bank...')
              : needsOnboarding
                ? 'Complete Verification'
                : 'Add Bank Account'}
        </Button>
      </Stack>
    </Box>
  );
};

ManualBankEntryForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
  sx: PropTypes.object,
};

export default ManualBankEntryForm;
